import {distributorUpdateT} from '@/lib/types/distributor.types';
import {FormFieldT, FormStepT} from '@/lib/types/form.types';

import {US_STATES_DATA} from '@/lib/jsonData/usStatesData';

/****************************************************************************
 * Form Fields
 ****************************************************************************/
export const distributorFormFields: FormFieldT<distributorUpdateT>[] = [
  {
    label: 'Distributor Name',
    name: 'distributor_name',
    options: {required: 'Distributor name is required'},
  },
  {
    label: 'Same As Speculator Email',
    name: 'distributor_same_as_speculator_email',
    custom: true,
    type: 'checkbox',
    defaultValue: true,
    watch: {
      enabled: true,
      onChange: ({value, setWatchEnabled}) => {
        setWatchEnabled(value === true);
      },
      configs: [
        {
          source: 'speculator_email',
          target: 'distributor_email',
          autoCopy: true,
        },
      ],
    },
  },
  {
    label: 'Distributor Email',
    name: 'distributor_email',
    type: 'email',
    options: {required: 'Distributor email is required'},
    watchConfig: {
      disableWhen: {
        field: 'distributor_same_as_speculator_email',
        is: true,
      },
    },
  },
  {
    label: 'Phone Number',
    name: 'distributor_phone',
    type: 'tel',
    options: {
      required: 'Phone number is required',
      pattern: {
        value: /^\d{10}$/,
        message: 'Please enter a valid 10-digit phone number',
      },
    },
  },
  {
    label: 'License Number',
    name: 'distributor_license_number',
    options: {required: 'License number is required'},
  },
  {
    label: 'METRC API Key',
    name: 'distributor_metrc_api_key',
    type: 'password',
    options: {required: 'METRC API key is required'},
  },
  {
    label: 'Geo Points',
    name: 'distributor_geo_points',
    type: 'text',
    options: {
      required: 'Geo points are required',
      pattern: {
        value: /^-?\d+\.?\d*,\s*-?\d+\.?\d*$/,
        message: 'Please enter valid geo coordinates (latitude,longitude)',
      },
    },
  },
  {
    type: 'checkbox',
    custom: true,
    label: 'Same as Speculator Address',
    name: 'distributor_same_as_speculator_address',
    defaultValue: true,
    watch: {
      enabled: true,
      onChange: ({value, setWatchEnabled}) => {
        setWatchEnabled(value === true);
      },
      configs: [
        {source: 'speculator_address', target: 'distributor_address'},
        {source: 'speculator_address2', target: 'distributor_address2'},
        {source: 'speculator_city', target: 'distributor_city'},
        {source: 'speculator_state', target: 'distributor_state'},
        {source: 'speculator_postal_code', target: 'distributor_postal_code'},
      ],
    },
  },
  {
    label: 'Address 1',
    name: 'distributor_address',
    options: {required: 'Address is required'},
    watchConfig: {
      disableWhen: {
        field: 'distributor_same_as_speculator_address',
        is: true,
      },
    },
  },
  {
    label: 'Address 2',
    name: 'distributor_address2',
    watchConfig: {
      disableWhen: {
        field: 'distributor_same_as_speculator_address',
        is: true,
      },
    },
  },
  {
    label: 'City',
    name: 'distributor_city',
    options: {required: 'City is required'},
    watchConfig: {
      disableWhen: {
        field: 'distributor_same_as_speculator_address',
        is: true,
      },
    },
  },
  {
    label: 'State',
    type: 'select',
    name: 'distributor_state',
    placeholder: 'Select State',
    selectOptions: US_STATES_DATA,
    options: {
      required: 'State is required',
      validate: value => !!US_STATES_DATA.find(state => state.value === value),
    },
    watchConfig: {
      disableWhen: {
        field: 'distributor_same_as_speculator_address',
        is: true,
      },
    },
  },
  {
    label: 'Postal Code',
    name: 'distributor_postal_code',
    options: {
      required: 'Postal code is required',
      pattern: {value: /^\d{5}$/, message: 'Invalid postal code'},
    },
    watchConfig: {
      disableWhen: {
        field: 'distributor_same_as_speculator_address',
        is: true,
      },
    },
  },
  {
    label: 'Notes',
    name: 'distributor_notes',
    type: 'textarea',
  },
];

export type DistributorFieldNamesT = (typeof distributorFormFields)[number]['name'];

/****************************************************************************
 * Form Steps
 ****************************************************************************/
export const distributorFormSteps: FormStepT<DistributorFieldNamesT> = [
  {
    step: 0,
    name: 'personal',
    label: 'Distributor Information',
    fields: [
      'distributor_name',
      'distributor_same_as_speculator_email',
      'distributor_email',
      'distributor_phone',
      'distributor_license_number',
      'distributor_metrc_api_key',
      'distributor_geo_points',
      'distributor_notes',
    ],
  },
  {
    step: 1,
    name: 'address',
    label: 'Distributor Address',
    fields: [
      'distributor_same_as_speculator_address',
      'distributor_address',
      'distributor_address2',
      'distributor_city',
      'distributor_state',
      'distributor_postal_code',
    ],
  },
];
